// Paste-cleanup engine shared by every RTE surface (Workspace, Mail Merge,
// Sequences, and any future editor built on RichTextEditor.js).
//
// WHY THIS FILE EXISTS: pasting from Microsoft Word, Outlook, Google Docs,
// or a webpage delivers HTML thick with source-specific cruft — `mso-*`
// inline style properties, Word's XML namespace tags (`<o:p>`, `<w:...>`),
// `<style>`/`<meta>` fragments, deeply nested `<span style="...">` soup,
// and one empty paragraph per blank line — none of which this app's editor
// (or an email client, later) has any use for. Before this module, every
// surface used Quill's stock clipboard handling with zero app-level
// cleanup (confirmed via audit — see RTE_PASTE_EMAIL_PLAN.md), so all of
// that cruft landed directly in composed content, causing exactly the
// "unnecessary gaps / incorrect wrapping / unexpected paragraph breaks"
// the user reported.
//
// `cleanPastedHtml(rawHtml, mode)` strips the cruft while preserving every
// format this app's Word ribbon actually understands (paragraphs, line
// breaks, headings, bold/italic/underline/strike, font family/size/color,
// highlight, alignment, indentation, lists, links, images — tables are a
// documented Quill platform limitation, handled best-effort by Quill's own
// generic matchers, same as before this module existed) — and implements
// Word/Outlook's real 4 Paste Special modes:
//   "keep"  — Keep Source Formatting: preserve everything above as-is.
//   "merge" — Merge Formatting: preserve structure (bold/italic/underline/
//             strike/lists/links/alignment) but drop font family/size/
//             color so pasted text adopts the destination's typography.
//   "match" — Match Destination Formatting: drop ALL character formatting
//             and flatten headings/quotes to plain paragraphs, so pasted
//             text fully adopts whatever formatting is active at the
//             cursor. Structure (paragraphs/lists/links) is kept.
//   "text"  — Keep Text Only: handled by the caller via the plain-text
//             clipboard read (unchanged, already correct — see
//             WordRibbon.js's doPasteSpecial).
//
// FONT-FAMILY AND UNDERLINE TRANSLATION (added after a confirmed bug report
// that pasted formatting was "collapsing"): this app's `font` and
// `underlinestyle` formats are registered as Class-only Attributors
// (wordFormats.js) with no Style-scoped counterpart. Quill's HTML-to-Delta
// clipboard conversion only ever recognizes a format through a registered
// Attributor's own matching logic — a Class Attributor matches by reading
// `element.classList` for its own prefix, never by reading a raw inline
// style. So a pasted `style="font-family:Arial"` or
// `style="text-decoration:underline"` — exactly what Word/Google Docs
// clipboard HTML actually contains — was being silently discarded during
// conversion, even though the rest of this module dutifully preserved it as
// inline style text right up until that point. Confirmed with a real
// Quill+jsdom round-trip test, not just by reading the code: bold/italic/
// color/size all survived a raw paste; font-family and CSS-based underline
// did not, while a literal `<u>` tag did. `matchFontFamily`/`detectUnderline`
// below translate the raw CSS this app can already produce ITSELF via its
// own ribbon (the exact `ql-font-*`/`ql-u-*` classes, optionally wrapped in
// a real `<u>` tag for base underline) — the same representation this
// editor's own saved documents already round-trip correctly — so pasted
// formatting now survives the exact same conversion path unchanged.
import { FONT_LIST, UNDERLINE_STYLES } from "./wordFormats";

// Regex pre-pass on the raw string, BEFORE DOM parsing — more predictable
// across browsers than relying on the HTML parser for malformed/namespaced
// markup (a literal `<o:p>` isn't valid HTML and different engines handle
// it differently once it's already in the DOM).
function stripRawCruft(html) {
  return String(html)
    .replace(/<!--[\s\S]*?-->/g, "") // HTML + MSO conditional comments
    .replace(/<\/?(?:o|w|v|m):[a-zA-Z0-9]+(?:\s[^>]*)?>/gi, "") // Office XML namespaced tags
    .replace(/<style[\s\S]*?<\/style>/gi, "")
    .replace(/<script[\s\S]*?<\/script>/gi, "")
    .replace(/<link[^>]*>/gi, "")
    .replace(/<meta[^>]*>/gi, "")
    .replace(/<xml[\s\S]*?<\/xml>/gi, "")
    .replace(/<!\[if[\s\S]*?\]>/gi, "")
    .replace(/<!\[endif\]>/gi, "");
}

// The only inline style properties this app's formats ever need — every
// mso-*/panose-*/-ms-* Office property, and every layout property a random
// webpage might carry (position, z-index, box-shadow, transform, ...),
// gets dropped here regardless of mode.
const ALLOWED_STYLE_PROPS = new Set([
  "font-family", "font-size", "font-weight", "font-style",
  "text-decoration", "text-decoration-line", "text-decoration-style", "text-decoration-thickness",
  "color", "background-color", "text-align",
  "padding-left", "margin-left", "margin-top", "margin-bottom", "line-height",
  "border", "text-shadow", "-webkit-text-stroke",
]);

// Character-level formatting properties — additionally stripped for
// "merge" and "match" modes so pasted text adopts destination typography.
const CHAR_STYLE_PROPS = new Set([
  "font-family", "font-size", "font-weight", "font-style", "color", "background-color",
  "text-shadow", "-webkit-text-stroke", "text-decoration", "text-decoration-line",
  "text-decoration-style", "text-decoration-thickness",
]);

const KEPT_ATTRS = new Set(["style", "class", "href", "src", "alt", "width", "height", "colspan", "rowspan", "target"]);

// ---- Spacing caps (the "lot of gaps" report) --------------------------
//
// Word and Google Docs paragraphs routinely carry their own generous
// "space after paragraph" (commonly 8-12pt, sometimes much more) and/or an
// expanded line-height, baked in as inline margin-top/margin-bottom/
// line-height on every <p>. None of that is cruft the way mso-* properties
// are — it's real formatting ALLOWED_STYLE_PROPS above deliberately keeps —
// but preserving it VERBATIM means a multi-paragraph paste compounds every
// paragraph's own spacing habit on top of the next, which is exactly what
// reads as "way more gap than I'd get from typing this myself" even though
// no single paragraph's margin looks unreasonable in isolation. Capping
// (not zeroing) keeps genuinely modest spacing untouched and only reins in
// values that would visibly balloon.
const PT_TO_PX = 96 / 72, IN_TO_PX = 96, CM_TO_PX = 96 / 2.54, MM_TO_PX = 96 / 25.4, EM_TO_PX = 16;
const MAX_PARAGRAPH_MARGIN_PX = 12; // roughly this app's own "Normal" paragraph spacing
const MAX_LINE_HEIGHT_PX = 40;
const MAX_LINE_HEIGHT_UNITLESS = 2.2;
const MAX_LINE_HEIGHT_PERCENT = 220;

function lengthToPx(numStr, unit) {
  const n = parseFloat(numStr);
  if (!Number.isFinite(n)) return null;
  switch (unit) {
    case "px": return n;
    case "pt": return n * PT_TO_PX;
    case "in": return n * IN_TO_PX;
    case "cm": return n * CM_TO_PX;
    case "mm": return n * MM_TO_PX;
    case "em": return n * EM_TO_PX;
    default: return null;
  }
}

function capLengthValue(raw, maxPx) {
  const m = /^(-?[\d.]+)\s*(px|pt|in|cm|mm|em)?$/.exec(raw.trim());
  if (!m) return raw; // unrecognized shape ("auto", a CSS var, ...) — leave untouched rather than risk mangling it
  const unit = m[2] || "px";
  const px = lengthToPx(m[1], unit);
  if (px == null || px <= maxPx) return raw;
  // Re-expressed in the SAME unit the source used, so e.g. a 24pt margin
  // becomes ~9pt rather than switching the whole paragraph to a different
  // unit than its neighbors (which is otherwise harmless but needlessly
  // inconsistent-looking if the user later inspects/edits it).
  const scale = px / parseFloat(m[1]);
  return `${Math.round((maxPx / scale) * 100) / 100}${unit}`;
}

function capLineHeightValue(raw) {
  const trimmed = raw.trim();
  const pct = /^(-?[\d.]+)%$/.exec(trimmed);
  if (pct) {
    const n = parseFloat(pct[1]);
    return n > MAX_LINE_HEIGHT_PERCENT ? `${MAX_LINE_HEIGHT_PERCENT}%` : raw;
  }
  const unitless = /^(-?[\d.]+)$/.exec(trimmed);
  if (unitless) {
    const n = parseFloat(unitless[1]);
    return n > MAX_LINE_HEIGHT_UNITLESS ? String(MAX_LINE_HEIGHT_UNITLESS) : raw;
  }
  return capLengthValue(raw, MAX_LINE_HEIGHT_PX);
}

function cleanStyleAttr(el, mode) {
  const style = el.getAttribute("style");
  if (!style) return;
  const kept = [];
  style.split(";").forEach(decl => {
    const idx = decl.indexOf(":");
    if (idx === -1) return;
    const prop = decl.slice(0, idx).trim().toLowerCase();
    let val = decl.slice(idx + 1).trim();
    if (!prop || !val) return;
    if (!ALLOWED_STYLE_PROPS.has(prop)) return;
    if ((mode === "merge" || mode === "match") && CHAR_STYLE_PROPS.has(prop)) return;
    if (prop === "margin-top" || prop === "margin-bottom") val = capLengthValue(val, MAX_PARAGRAPH_MARGIN_PX);
    else if (prop === "line-height") val = capLineHeightValue(val);
    kept.push(prop + ": " + val);
  });
  if (kept.length) el.setAttribute("style", kept.join("; "));
  else el.removeAttribute("style");
}

function cleanClassAttr(el, mode) {
  const cls = el.getAttribute("class");
  if (!cls) return;
  // Only our own `ql-*` classes carry meaning outside the page they were
  // rendered on (they map to formats registered app-wide by
  // wordFormats.js) — every other class (Word's MsoNormal/c1/c2, Google
  // Docs' generated classes, a webpage's own CSS classes) means nothing
  // without a stylesheet that will never travel with pasted content.
  let tokens = cls.split(/\s+/).filter(Boolean).filter(t => t.startsWith("ql-"));
  if (mode === "merge" || mode === "match") {
    tokens = tokens.filter(t => !t.startsWith("ql-font") && !t.startsWith("ql-u-") && !t.startsWith("ql-texteffect"));
  }
  if (tokens.length) el.setAttribute("class", tokens.join(" "));
  else el.removeAttribute("class");
}

const FLATTEN_ON_MATCH = ["b", "strong", "i", "em", "u", "s", "strike", "font", "sup", "sub"];

function isBlankParagraph(el) {
  // Word's own "blank line" paste is almost always a lone &nbsp;
  // (` `), which String.prototype.trim() does not treat as
  // whitespace — normalize it first so real blank lines are detected.
  const text = el.textContent.replace(/ /g, " ").trim();
  return !text && !el.querySelector("img,br") && el.children.length === 0;
}

// Reads one declaration out of a raw `style="..."` attribute string without
// needing a live stylesheet/CSSOM -- the pasted DOM here is detached and
// never attached to `document`, so `getComputedStyle` isn't reliable on it,
// and different browsers' clipboard HTML capitalizes/spaces declarations
// inconsistently enough that a straight string split is more predictable
// than depending on `element.style.<camelCase>` normalization.
function getStyleProp(styleAttr, prop) {
  if (!styleAttr) return "";
  const decls = styleAttr.split(";");
  for (const d of decls) {
    const idx = d.indexOf(":");
    if (idx === -1) continue;
    if (d.slice(0, idx).trim().toLowerCase() === prop) return d.slice(idx + 1).trim().toLowerCase();
  }
  return "";
}

// First font in the stack, unquoted/lowercased, matched against this app's
// own curated FONT_LIST by label ("Times New Roman") or slug ("timesnewroman")
// -- covers every standard system font Word/Docs/a webpage would realistically
// send, which is the same list this app's own font picker already offers.
// Deliberately does NOT fall back to preserving an unmatched font verbatim:
// the whole point of wordFormats.js consolidating font handling into one
// whitelisted picker was to stop every surface silently drifting to
// different, uncontrolled font sets (see that file's own header comment) --
// an unmatched paste falls back to this app's default font, same as before
// this fix, rather than reintroducing that problem through the back door.
function matchFontFamily(styleAttr) {
  const raw = getStyleProp(styleAttr, "font-family");
  if (!raw) return null;
  const first = raw.split(",")[0].trim().replace(/^["\']|["\']$/g, "").toLowerCase();
  if (!first) return null;
  return FONT_LIST.find(f => f.label.toLowerCase() === first || f.value === first.replace(/\s+/g, "")) || null;
}

const UNDERLINE_VARIANT_VALUES = new Set(UNDERLINE_STYLES.map(s => s.value).filter(v => v !== "single"));

// Detects CSS-based underline (`text-decoration`/`text-decoration-line`
// containing "underline") the way Word/Google Docs actually express it --
// and, when present, which of this app's own underline-style variants
// (double/thick/dotted/dashed/wavy) it corresponds to, if any. Returns
// `null` (no underline), or `{ variant: null }` (plain single underline),
// or `{ variant: "double" }` etc.
function detectUnderline(styleAttr) {
  if (!styleAttr) return null;
  const shorthand = getStyleProp(styleAttr, "text-decoration");
  const line = getStyleProp(styleAttr, "text-decoration-line");
  const hasUnderline = /(^|\s)underline(\s|$)/.test(shorthand) || /(^|\s)underline(\s|$)/.test(line);
  if (!hasUnderline) return null;
  let variant = getStyleProp(styleAttr, "text-decoration-style");
  if (!UNDERLINE_VARIANT_VALUES.has(variant)) variant = null;
  if (!variant) {
    const thickness = getStyleProp(styleAttr, "text-decoration-thickness");
    const px = parseFloat(thickness);
    if (thickness && Number.isFinite(px) && (thickness.includes("px") ? px >= 3 : px >= 2)) variant = "thick";
  }
  return { variant };
}

function cleanNode(root, mode) {
  root.querySelectorAll("style,script,meta,link,title,xml,head").forEach(el => el.remove());

  Array.from(root.querySelectorAll("*")).forEach(el => {
    Array.from(el.attributes).forEach(attr => {
      if (!KEPT_ATTRS.has(attr.name.toLowerCase())) el.removeAttribute(attr.name);
    });

    // Must run BEFORE cleanStyleAttr/cleanClassAttr below -- it reads the
    // still-raw style attribute to decide what to translate, then leaves
    // the actual mode-based keep/merge/match filtering to those two
    // functions exactly as before (a freshly-injected ql-font-* class is
    // filtered out for merge/match the same as a class that arrived
    // already on the element -- no special-casing needed there).
    const rawStyle = el.getAttribute("style");
    if (rawStyle) {
      const fontMatch = matchFontFamily(rawStyle);
      if (fontMatch) {
        el.classList.add("ql-font-" + fontMatch.value);
        // A leftover raw font-family style ALONGSIDE the class it now
        // matches isn't harmless redundancy the way it looked like it
        // would be — empirically, Quill's clipboard matcher pipeline
        // drops BOTH the class-based font format and the (unmatched)
        // style when a node carries conflicting information for the same
        // property, rather than preferring one. The class is what
        // actually carries the format going forward, so the now-redundant
        // style has to go, not just optionally could.
        if (el.style) el.style.removeProperty("font-family");
      }

      const underline = detectUnderline(rawStyle);
      if (underline) {
        // Wrap this element's OWN children (not the element itself) in a
        // real <u> -- Quill's native underline format is tag-matched, not
        // style-matched (confirmed: a literal <u> survives conversion, a
        // CSS-only text-decoration does not), and wrapping contents rather
        // than the element itself keeps this valid regardless of whether
        // el is inline or block. The decorative ql-u-<variant> class (vs.
        // plain single underline) is only kept for "keep" mode -- merge
        // mode's documented contract preserves the underline itself but
        // not its typographic flourish, and match mode gets both stripped
        // right back out by the existing <u>-flattening pass a few lines
        // below regardless of what's set here, so no mode check is needed
        // for match specifically.
        const wrapper = document.createElement("u");
        if (underline.variant && mode === "keep") wrapper.className = "ql-u-" + underline.variant;
        while (el.firstChild) wrapper.appendChild(el.firstChild);
        el.appendChild(wrapper);
        // Avoid redundant (harmless, but noisy) duplicate underline styling
        // surviving on the outer element too, now that a real <u> carries it.
        if (el.style) {
          el.style.removeProperty("text-decoration");
          el.style.removeProperty("text-decoration-line");
          el.style.removeProperty("text-decoration-style");
          el.style.removeProperty("text-decoration-thickness");
        }
      }
    }

    cleanStyleAttr(el, mode);
    cleanClassAttr(el, mode);
  });

  if (mode === "match") {
    // Destination formatting fully wins: headings/quotes become plain
    // paragraphs, and inline formatting tags are unwrapped down to their
    // text content, one pass per tag type (nested runs need their own
    // iteration since unwrapping a parent doesn't remove a child of the
    // same tag name already queued in the same querySelectorAll pass).
    Array.from(root.querySelectorAll("h1,h2,h3,h4,h5,h6,blockquote")).forEach(el => {
      const p = document.createElement("p");
      p.innerHTML = el.innerHTML;
      el.replaceWith(p);
    });
    FLATTEN_ON_MATCH.forEach(tag => {
      Array.from(root.querySelectorAll(tag)).forEach(el => el.replaceWith(...el.childNodes));
    });
  }

  // Word/Google-Docs "<span style=\"mso-...\">text</span>" soup — once
  // mso-* is stripped there's nothing left to keep the span for.
  Array.from(root.querySelectorAll("span")).forEach(el => {
    if (!el.getAttribute("style") && !el.getAttribute("class")) el.replaceWith(...el.childNodes);
  });

  // Drop fully-empty paragraphs/divs — Word/Docs commonly emit one per
  // blank line in the source; intentional blank-line spacing (an actual
  // <p><br></p> Quill would itself produce) is untouched since a lone <br>
  // child means `isBlankParagraph` returns false for it here, and the
  // separate run-collapsing pass below caps only *excessive* runs, not
  // single/double intentional gaps.
  Array.from(root.querySelectorAll("p,div")).forEach(el => {
    if (isBlankParagraph(el)) el.remove();
  });
}

function normalizeBlankParagraphs(html) {
  let out = html.replace(/<p>\s*<\/p>/g, "");
  out = out.replace(/(?:<p><br><\/p>\s*){3,}/g, "<p><br></p><p><br></p>");
  return out;
}

// mode: "keep" | "merge" | "match". Runs entirely against a detached DOM
// node — never touches the live editor. Real-world Word/Outlook clipboard
// HTML is far messier than any synthetic test fixture (VML shapes, content
// controls, deeply-nested conditional-comment sandwiches for image
// fallbacks, etc.) — a bug in the DOM-cleaning pass here must never take
// the whole paste down with it, so the DOM-manipulation step is wrapped
// separately from the (much simpler, regex-only) cruft-stripping step:
// if cleanNode() throws on some pathological input, we still return the
// regex-stripped-but-otherwise-unprocessed HTML rather than nothing.
export function cleanPastedHtml(rawHtml, mode) {
  if (!rawHtml) return "";
  if (typeof document === "undefined") return rawHtml;
  const stripped = stripRawCruft(rawHtml);
  try {
    const container = document.createElement("div");
    container.innerHTML = stripped;
    cleanNode(container, mode || "keep");
    return normalizeBlankParagraphs(container.innerHTML);
  } catch (e) {
    console.error("cleanPastedHtml: DOM cleanup pass failed, falling back to regex-only cleanup", e);
    return normalizeBlankParagraphs(stripped);
  }
}

export function escapeHtml(str) {
  return String(str || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

// Inserts already-prepared HTML, replacing `range` (defaults to the current
// selection) — mirrors Quill's own internal clipboard `onPaste` delta math
// exactly (see node_modules/quill/modules/clipboard.js) so cursor placement
// behaves identically to native Quill paste. Returns the {index, length} of
// the newly-inserted content, so a caller (the "Paste Options" smart tag —
// see PasteOptions.js) can re-clean the exact same span with a different
// mode after the fact, the way Word's own paste-options tag lets you
// retroactively switch Keep Source Formatting / Merge Formatting / etc.
// without knowing where the cursor has since moved.
export function insertCleanedHtml(quill, html, range) {
  const r = range || quill.getSelection(true) || { index: quill.getLength(), length: 0 };
  try {
    return insertViaDelta(quill, html, r);
  } catch (e) {
    console.error("insertCleanedHtml: primary insert failed, retrying as plain text", e);
    try {
      const div = document.createElement("div");
      div.innerHTML = html;
      const plain = escapeHtml(div.textContent || "").replace(/\n/g, "<br>");
      return insertViaDelta(quill, plain, r);
    } catch (e2) {
      // Both the formatted insert AND the plain-text fallback failed —
      // give up without throwing (a broken paste must never crash the
      // editor or leave it in a half-updated state) and report the exact
      // range so the caller (e.g. the Paste Options widget) can still
      // decide what to do rather than blow up itself.
      console.error("insertCleanedHtml: plain-text fallback also failed — leaving document untouched", e2);
      return { index: r.index, length: 0 };
    }
  }
}

function insertViaDelta(quill, html, r) {
  const Delta = quill.constructor.import ? quill.constructor.import("delta") : null;
  const pasted = quill.clipboard.convert(html);
  if (Delta) {
    const delta = new Delta().retain(r.index).concat(pasted).delete(r.length);
    quill.updateContents(delta, "user");
    const newIndex = delta.length() - r.length;
    quill.setSelection(newIndex, "silent");
    return { index: r.index, length: pasted.length() };
  }
  // Fallback if Delta isn't importable in this Quill build (matches the
  // same defensive fallback pattern used by wordFormats.js's
  // sortSelectionLines).
  if (r.length) quill.deleteText(r.index, r.length, "user");
  quill.clipboard.dangerouslyPasteHTML(r.index, html, "user");
  return { index: r.index, length: html.length };
}

// ---- Default paste mode (Word's "Set Default Paste..." setting) --------
// Persisted per-browser so a user's choice (e.g. "always Merge Formatting")
// sticks across sessions and every surface, the same way Word's Options >
// Advanced > Cut/Copy/Paste settings are a single app-wide preference.
const DEFAULT_PASTE_MODE_KEY = "sdr_default_paste_mode";
const VALID_MODES = ["keep", "merge", "match", "text"];

export function getDefaultPasteMode() {
  try {
    const v = window.localStorage.getItem(DEFAULT_PASTE_MODE_KEY);
    return VALID_MODES.includes(v) ? v : "keep";
  } catch (e) {
    return "keep";
  }
}

export function setDefaultPasteMode(mode) {
  if (!VALID_MODES.includes(mode)) return;
  try { window.localStorage.setItem(DEFAULT_PASTE_MODE_KEY, mode); } catch (e) { /* storage unavailable — setting just won't persist across sessions */ }
}
